import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { keyframes } from "@emotion/react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { tokens } from "@/theme/tokens";
import { CodeBlock } from "./CodeBlock";
import { ReasoningDetails, REASONING_LANGUAGE } from "./ReasoningDetails";
import { StreamingProvider } from "./StreamingContext";
import { MARKDOWN_ROOT_ATTR, TableExportMenu } from "./TableExportMenu";
// The minimal HAST node shape this file walks and rewrites — enough to do it
// without a unist typings dependency. Owned by `tableExtract.ts`, which needs
// the identical shape and is pure (no React, no MUI), so it can be imported
// from either side. There used to be a private copy here; two copies of a
// structural type drift silently, and one already had.
import type { HastNode } from "./tableExtract";
import { UserQuestionProvider } from "./UserQuestionContext";
import { useEmbedApiAuth } from "@/api/embedApiContext";
import "katex/dist/katex.min.css";

// A connector-OAuth "Sign in" link points at
// /plugins/v1/connectors/{slug}/authorize/start (a GET). In the SPA that's
// fine (cookie session). But INSIDE THE EMBED a link click is a top-level
// navigation that carries only the browser cookie — NOT the embed userJWT — so
// OAuth would run as whatever SPA session the top window holds, storing the
// token under the wrong identity (the embed user then still sees "not
// connected"). Detect that link and, when we're in the embed (bearer present),
// intercept the click: XHR POST .../authorize WITH the bearer (so the server
// bakes the EMBED identity into the OAuth state), then window.open the returned
// url. Everything else renders as a normal new-tab link.
const _AUTHORIZE_START_RE =
  /\/plugins\/v1\/connectors\/([^/]+)\/authorize\/start\/?$/;

function MarkdownLink(props: any) {
  const { href, children, ...rest } = props;
  const embed = useEmbedApiAuth();
  const m = typeof href === "string" ? href.match(_AUTHORIZE_START_RE) : null;

  if (embed && m) {
    const slug = m[1];
    const onClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      try {
        const base = embed.apiUrl.replace(/\/+$/, "");
        const token = await embed.getToken();
        const r = await fetch(
          `${base}/plugins/v1/connectors/${encodeURIComponent(slug)}/authorize`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: "{}",
          },
        );
        if (!r.ok) throw new Error(String(r.status));
        const { authorize_url } = await r.json();
        window.open(authorize_url, "_blank", "noopener,noreferrer");
      } catch {
        // Fall back to the plain link so the user isn't stuck.
        window.open(href, "_blank", "noopener,noreferrer");
      }
    };
    return (
      <a href={href} onClick={onClick} {...rest}>
        {children}
      </a>
    );
  }

  // Default: open in a new top-level window (also breaks the embed iframe out
  // for any other external link, e.g. docs). rel guards the opener.
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

export interface MarkdownViewProps {
  source: string;
  /** While true (the live turn's still-streaming text part), wrap each prose
   *  word in a `.md-word` span so newly-mounted words softly fade in — the
   *  Claude.ai-style reveal. Off for history/settled turns, which render plain
   *  (no spans, no lingering animation). The staggered cadence comes from
   *  useSmoothedText metering the text upstream; this only supplies the fade. */
  streaming?: boolean;
  /** The user turn this markdown answers, published to the rendered tree so a
   *  table override can name the question its export came from. Optional: the
   *  artifact and subagent-preview call sites have no user turn to attribute,
   *  and must not invent one. Keep it a scalar — an inline object or callback
   *  here would defeat the memo below and re-parse on every streamed token. */
  userQuestion?: string;
}

// Hoisted so the plugin/component config is referentially stable across
// renders. Re-creating these arrays inline on every render makes react-markdown
// treat the config as "changed" and re-runs the full parse — the main cause of
// streaming jank. Defined once at module scope, they never change identity.
// singleDollarTextMath: false disables single-`$…$` inline math so prose
// currency ("$260K") renders literally instead of being parsed as LaTeX (which
// strips the `$` and collapses surrounding whitespace). Genuine math still
// works via `$$…$$` block syntax.
const REMARK_PLUGINS = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]] as const;
const REHYPE_PLUGINS = [rehypeKatex];

/** Word boundary split that KEEPS the whitespace runs (odd indices) so the
 *  reconstructed prose reads identically — we only wrap the word runs. */
const WORD_SPLIT = /(\s+)/;

/** True for subtrees whose text must NOT be split into word spans: code (the
 *  `code` component reads String(children) and multiline goes to CodeBlock —
 *  spans would mangle both) and rendered KaTeX (rehype-katex runs first, so its
 *  `.katex*` output is already present and must stay atomic). */
function isFadeExcluded(node: HastNode): boolean {
  if (node.type !== "element") return false;
  if (node.tagName === "code" || node.tagName === "pre") return true;
  const cls = node.properties?.className;
  return Array.isArray(cls) && cls.some((c) => String(c).startsWith("katex"));
}

/** Turn a text value into alternating whitespace text nodes and `.md-word`
 *  span elements — one span per whitespace-delimited word. */
function toWordNodes(value: string): HastNode[] {
  const out: HastNode[] = [];
  for (const part of value.split(WORD_SPLIT)) {
    if (part === "") continue;
    if (/^\s+$/.test(part)) {
      out.push({ type: "text", value: part });
    } else {
      out.push({
        type: "element",
        tagName: "span",
        properties: { className: ["md-word"] },
        children: [{ type: "text", value: part }],
      });
    }
  }
  return out;
}

/** rehype transform: wrap prose words in `.md-word` spans, skipping excluded
 *  subtrees. Runs AFTER rehype-katex so math is already expanded (and skipped).
 *  Hand-rolled recursion (rather than unist-util-visit) keeps this dependency
 *  free and lets us carry the "inside an excluded subtree" flag down the tree. */
function rehypeWordFade() {
  const walk = (node: HastNode, excluded: boolean) => {
    if (!node.children) return;
    const excludedHere = excluded || isFadeExcluded(node);
    const next: HastNode[] = [];
    for (const child of node.children) {
      if (child.type === "text" && !excludedHere) {
        next.push(...toWordNodes(child.value ?? ""));
      } else {
        if (child.type === "element" || child.type === "root") {
          walk(child, excludedHere);
        }
        next.push(child);
      }
    }
    node.children = next;
  };
  return (tree: HastNode) => walk(tree, false);
}

const REHYPE_PLUGINS_STREAMING = [rehypeKatex, rehypeWordFade];

// Soft materialize: newly-mounted word spans fade up from transparent with a
// slight blur (opacity/blur only — no transform, so spans stay pure inline and
// don't disturb line wrapping/baseline). Runs once on mount; React reuses
// already-shown words across the append-only re-renders, so they don't replay.
const wordIn = keyframes`
  from { opacity: 0; filter: blur(4px); }
  to   { opacity: 1; filter: blur(0); }
`;
const MARKDOWN_COMPONENTS = {
  // MarkdownLink supersedes the plain new-window `a` from #183: it keeps the
  // target=_blank/rel new-window default for every link (so external OAuth
  // pages break out of the iframe instead of loading blocked/blank) AND adds
  // the embed connector-OAuth click interceptor on top.
  a: MarkdownLink,
  code(props: any) {
    const { className, children, ...rest } = props;
    const match = /language-(\w+)/.exec(className ?? "");
    const text = String(children).replace(/\n$/, "");
    // An expandable block, not code: the invoice review's reasoning and sources.
    if (match?.[1] === REASONING_LANGUAGE) {
      return <ReasoningDetails value={text} />;
    }
    // Any multiline code — including a bare ``` fence with no language tag
    // (models emit those for ASCII/tree output) — gets the full CodeBlock
    // treatment; only single-line spans stay inline.
    if (text.includes("\n")) {
      return <CodeBlock language={match?.[1]} value={text} />;
    }
    return <code className={className} {...rest}>{children}</code>;
  },
  // A fenced block renders as <pre><code>; a reasoning fence returns a
  // <details>, which must not sit inside a <pre> (monospace, no wrapping).
  // Every other fence keeps the default <pre>.
  pre({ node, children, ...props }: any) {
    const first = node?.children?.[0];
    const classes = first?.properties?.className;
    const isReasoning =
      Array.isArray(classes) && classes.includes(`language-${REASONING_LANGUAGE}`);
    return isReasoning ? <>{children}</> : <pre {...props}>{children}</pre>;
  },
  // Wrap tables so a wide table scrolls inside its own lane instead of pushing
  // the whole reply column sideways. Cell/header styling lives in the parent
  // Box sx (`& table`, `& th`, `& td`).
  //
  // TableExportMenu wraps that lane (rather than sitting inside it) so its
  // control has a positioning ancestor that does not scroll, and reads the
  // `node` this override has always destructured but never used — the table's
  // hast, which is what the export is built from.
  table({ node, ...props }: any) {
    return (
      <TableExportMenu node={node as HastNode | undefined}>
        <Box sx={{ overflowX: "auto", maxWidth: "100%", my: 1.5 }}>
          <table {...props} />
        </Box>
      </TableExportMenu>
    );
  },
};

// Marks one message's rendered markdown, so the export menu can number the
// tables within it (`artie-table-….xlsx`, `-2`, `-3`). Hoisted: a fresh object
// here would churn the root Box's props on every streamed token.
const ROOT_MARKER = { [MARKDOWN_ROOT_ATTR]: "" };

function MarkdownViewImpl({ source, streaming = false, userQuestion }: MarkdownViewProps) {
  return (
    <Box
      {...ROOT_MARKER}
      sx={{
        // Streaming reveal: each freshly-mounted word fades/blurs in. Under
        // prefers-reduced-motion the animation is dropped so words appear at
        // full opacity immediately (the `md-word` wrapping is harmless there).
        "& .md-word": {
          animation: `${wordIn} ${tokens.motion.duration.normal}ms cubic-bezier(${tokens.motion.easing.smooth.join(",")}) both`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .md-word": { animation: "none" },
        },
        // "Noto Color Emoji" is appended to the body stack so emoji glyphs
        // still resolve on OSes that ship that font (Android/Linux) instead
        // of falling back further; on OSes without it the browser just uses
        // the native emoji font. Listed LAST: browsers resolve font-family
        // per glyph, so prose uses the inherited text font (self-hosted
        // Hanken Grotesk, see src/theme/tokens.ts) and only emoji codepoints
        // — which the text fonts lack — fall through.
        fontFamily: '"Hanken Grotesk Variable", "Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif, "Noto Color Emoji"',
        // Breathing body text: relaxed line-height + real space between blocks.
        lineHeight: 1.65,
        "& p": { my: 1 },
        "& pre": { my: 1.25 },
        // Lists get room to breathe; paragraphs nested in list items stay tight.
        "& ul, & ol": { my: 1, pl: 3 },
        "& li": { my: 0.4 },
        "& li > p": { my: 0 },
        // Section headings render in the emerald accent (new design): green,
        // bold, with clear space above so sections separate WITHOUT a rule.
        "& h1, & h2, & h3, & h4": {
          color: "primary.main",
          fontWeight: 700,
          letterSpacing: "-0.005em",
          mt: 2.5,
          mb: 0.75,
          lineHeight: 1.3,
        },
        "& h1": { fontSize: "1.15rem" },
        "& h2": { fontSize: "1.05rem" },
        "& h3, & h4": { fontSize: "1rem" },
        "& > :first-of-type": { mt: 0 },
        // Section divider (`---`): a short, centered, subtle grey line — sits
        // exactly between the two sections (equal margin) at a reduced width
        // rather than a full-bleed rule.
        "& hr": {
          border: 0,
          // A clearly-visible grey (theme `divider` is too faint to read);
          // derived from text color so it works in light and dark.
          borderTop: (t) => `1px solid ${alpha(t.palette.text.primary, 0.25)}`,
          width: 250,
          mx: "auto",
          my: 2.5,
        },
        // Tables: compact but padded, left-aligned, header underlined in the
        // brand accent, hairline row dividers, last row flush. The horizontal
        // scroll lane comes from the `table` component wrapper above.
        "& table": {
          borderCollapse: "collapse",
          width: "auto",
          fontSize: "0.9rem",
          lineHeight: 1.5,
        },
        "& th, & td": { textAlign: "left", py: 0.85, px: 1.5, verticalAlign: "top" },
        "& thead th": {
          fontWeight: 700,
          color: "text.primary",
          whiteSpace: "nowrap",
          borderBottom: (t) => `2px solid ${alpha(t.palette.primary.main, 0.35)}`,
        },
        "& tbody td": { borderBottom: (t) => `1px solid ${t.palette.divider}` },
        "& tbody tr:last-of-type td": { borderBottom: 0 },
        // Links in the terracotta tertiary — warm emphasis against teal headings.
        // (Bold text stays default-colored: answers are bold-heavy, so coloring
        // every <strong> would be noisy.)
        "& a": {
          color: "tertiary.main",
          textDecoration: "none",
          fontWeight: 600,
          "&:hover": { textDecoration: "underline" },
        },
      }}
    >
      {/* Provenance for anything the component overrides render. They live in
          a module-scope const (no hooks), so context is the only way down —
          for the turn's question and, the same way, for whether it is still
          streaming (the table export menu hides itself while it is). Both
          values are scalars, so neither costs the memo below anything. */}
      <UserQuestionProvider question={userQuestion ?? null}>
        <StreamingProvider streaming={streaming}>
          <ReactMarkdown
            remarkPlugins={REMARK_PLUGINS as any}
            rehypePlugins={streaming ? (REHYPE_PLUGINS_STREAMING as any) : REHYPE_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {source}
          </ReactMarkdown>
        </StreamingProvider>
      </UserQuestionProvider>
    </Box>
  );
}

// Memoized on (`source`, `streaming`, `userQuestion`). During streaming, a
// parent re-render that doesn't change this part's text is a no-op here —
// completed paragraphs/messages stop re-parsing on every token. The default
// shallow compare covers `streaming` too, so flipping it (turn settles)
// re-renders once into the plain, span-free tree; `userQuestion` is a scalar
// compared by value, so it costs the memo nothing.
export const MarkdownView = React.memo(MarkdownViewImpl);
